import { create } from "zustand";
import { persist } from "zustand/middleware";
import CryptoJS from "crypto-js";

import { Theme } from "@/entities/session/types";
import type { User } from "../../user";

interface SessionState {
  user: User | null;
  theme: Theme;
  setTheme: (user: Theme) => void;
  isAuthenticated: boolean;
  isRemember: boolean;
  setUser: (user: User | null) => void;
  logout: () => void;
  updateUser: (partialUser: Partial<User>) => void;
  accessToken: string | null;
  refreshToken: string | null;
  setAccessToken: (accessToken: string | null) => void;
  setRefreshToken: (refreshToken: string | null) => void;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setIsRemember: (isRemember: boolean) => void;
}

const SECRET_KEY = import.meta.env.VITE_ENCRYPT_TG_CODE;

export const useSessionStore = create(
  persist<SessionState>(
    (set) => ({
      permissions: [],
      user: null,
      isAuthenticated: false,
      isRemember: true,
      theme: Theme.Light,
      setTheme: (theme: Theme) => set({ theme: theme }),
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      updateUser: (partialUser) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...partialUser } : null,
        })),
      logout: () => {
        set(() => {
          localStorage.removeItem("master-teamlead-store");

          return {
            user: null,
            isAuthenticated: false,
            isRemember: true,
            accessToken: null,
            refreshToken: null,
          };
        });
        localStorage.removeItem("user-store");
      },
      setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
      accessToken: null,
      refreshToken: null,
      setAccessToken: (accessToken) => {
        set({ accessToken });
      },
      setIsRemember: (isRemember) => {
        set({ isRemember });
      },
      setRefreshToken: (refreshToken) => {
        set({ refreshToken });
      },
    }),
    {
      name: "user-store",

      storage: {
        getItem: (name: string) => {
          const item = localStorage.getItem(name);
          if (item) {
            try {
              const bytes = CryptoJS.AES.decrypt(item, SECRET_KEY);
              const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
              return decryptedData ? JSON.parse(decryptedData) : null;
            } catch (error) {
              console.error("Ошибка при расшифровке данных:", error);
              return null;
            }
          }
          return null;
        },

        setItem: (name: string, value: { state: SessionState }) => {
          try {
            const encryptedData = CryptoJS.AES.encrypt(
              JSON.stringify(value),
              SECRET_KEY,
            ).toString();
            localStorage.setItem(name, encryptedData);
          } catch (error) {
            console.error("Ошибка при шифровании данных:", error);
          }
        },
        removeItem: (name: string) => {
          localStorage.removeItem(name);
        },
      },

      onRehydrateStorage: () => (_, error) => {
        if (error) {
          console.error("Ошибка при восстановлении состояния:", error);
        }
      },
    },
  ),
);
